import type { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useRouteLoaderData } from '@remix-run/react';
import type { TRSSFeedElement } from '@shared/dream-components';
import { RSSFeedElement } from '@shared/dream-components';

import Text from '~/components/Text';
import useIsMobile from '~/hooks/useIsMobile';
import useRssApplePodcast from '~/hooks/useRssApplePodcast';
import useRssFeed from '~/hooks/useRssFeed';
import type { RootLoaderData } from '~/root';

interface Props {
  node: TRSSFeedElement;
  children: ReactNode | ReactNode[];
}

const ErrorBoundaryFallback = ({ message }: { message: string }) => (
  <div className="flex max-w-2xl flex-col space-y-2 py-8">
    <Text as="p" type="body" size="md" className="text-center leading-tight tracking-normal">
      {message}
    </Text>
  </div>
);

const RSSFeedBase = ({ node, children }: Props) => {
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const { rssFeed, isLoading, isError } = useRssFeed({
    url: node.attrs.url || '',
  });

  const isViewportMobile = useIsMobile();

  const { data: rssApplePodcastFeed } = useRssApplePodcast({
    itunesId: node.attrs.itunesId || '',
    count: node.attrs.showCount || 5,
    enabled: !!node.attrs.itunesId,
  });

  return (
    <RSSFeedElement
      element={{
        type: 'rss_feed',
        attrs: {
          ...node.attrs,
          isViewportMobile,
          data: rssFeed || node.attrs.data,
          isLoading,
          isError,
          applePodcastData: rssApplePodcastFeed || node.attrs.applePodcastData,
        },
        content: [{ id: 'empty-children', text: '' }],
      }}
      onClick={() => {}}
      rssFeedMap={site?.viewable_site_version?.customization?.rss_feed_map}
    >
      {children}
    </RSSFeedElement>
  );
};

const RSSFeed = ({ node, children }: Props) => {
  return (
    <ErrorBoundary fallbackRender={(props) => <ErrorBoundaryFallback message={props.error.message} />}>
      <RSSFeedBase node={node}>{children}</RSSFeedBase>
    </ErrorBoundary>
  );
};

export default RSSFeed;
